// src/components/ReturnsDrafts.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";

const CYL_ROUTE  = "/returns/new";
const PART_ROUTE = "/returns/parts/new";

const PAGE_SIZE = 25;
const FETCH_LIMIT_PER_COLLECTION = 200;

/** Active workflow statuses to show in Drafts (exclude submitted) */
const ACTIVE_STATUSES = [
  "new",
  "waiting_collection",
  "waiting_processing",
  "in_progress",
  "complete",
  "straggler",
];

/** Status labels + Tailwind colour classes */
const STATUS_META = {
  new:                 { label: "New",                    dot: "bg-gray-400",    badge: "bg-gray-100 text-gray-800 border-gray-200" },
  waiting_collection:  { label: "Waiting for collection", dot: "bg-amber-500",   badge: "bg-amber-100 text-amber-800 border-amber-200" },
  waiting_processing:  { label: "Waiting to be processed",dot: "bg-sky-500",     badge: "bg-sky-100 text-sky-800 border-sky-200" },
  in_progress:         { label: "In progress",            dot: "bg-indigo-600",  badge: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  complete:            { label: "Complete",               dot: "bg-emerald-600", badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  straggler:           { label: "Straggler",              dot: "bg-rose-600",    badge: "bg-rose-100 text-rose-800 border-rose-200" },
};
const statusOf = (v) =>
  STATUS_META[v] || {
    label: String(v || "—"),
    dot: "bg-gray-300",
    badge: "bg-gray-100 text-gray-800 border-gray-200",
  };

const typeBadge = (kind) =>
  kind === "parts"
    ? { text: "PART", cls: "bg-amber-100 text-amber-800 border-amber-200" }
    : { text: "CYL",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" };

/** sortable columns keys */
const SORT_KEYS = {
  TYPE: "type",
  STATUS: "status",
  CREATED: "createdAt",
  UPDATED: "updatedAt",
  REF: "ref",
  SRET: "sret",
  SERIAL_OR_PART: "serialOrPart",
  MODEL_OR_TITLE: "modelOrTitle",
  REASON: "reason",
  PROGRESS: "progress",
};

/** First-click default sort direction per column */
const DEFAULT_DIR = {
  [SORT_KEYS.TYPE]: "asc",
  [SORT_KEYS.STATUS]: "asc",
  [SORT_KEYS.CREATED]: "asc",   // oldest first
  [SORT_KEYS.UPDATED]: "desc",   // oldest first
  [SORT_KEYS.REF]: "desc",      // RN highest first
  [SORT_KEYS.SRET]: "asc",
  [SORT_KEYS.SERIAL_OR_PART]: "asc",
  [SORT_KEYS.MODEL_OR_TITLE]: "asc",
  [SORT_KEYS.REASON]: "asc",
  [SORT_KEYS.PROGRESS]: "desc", // highest first
};

/** utils */
const normalize = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const fmtDateTime = (d) => {
  if (!d) return "—";
  // dd/mm/yyyy hh:mm
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/* -------- sorting helpers -------- */
const safeLower = (s) => (s ? String(s).toLowerCase() : "");
// Treat null dates as very old so ASC puts them first, DESC last.
const toMs = (d) => (d instanceof Date ? d.getTime() : Number.NEGATIVE_INFINITY);
const statusRank = (s) => {
  const i = ACTIVE_STATUSES.indexOf(s || "");
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};
// Extract trailing digits for RN like "RN-000123" -> 123; NaN if none.
const rnNumber = (ref) => {
  const m = String(ref || "").match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : Number.NaN;
};
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const withDir = (res, dir) => (dir === "asc" ? res : -res);

export default function ReturnsDrafts() {
  const navigate = useNavigate();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qtext, setQtext] = useState("");
  const [rnQuery, setRnQuery] = useState(""); // exact RN numeric query
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);

  // sorting state (default to UPDATED with that column's default dir)
  const [sortBy, setSortBy] = useState(SORT_KEYS.UPDATED);
  const [sortDir, setSortDir] = useState(DEFAULT_DIR[SORT_KEYS.UPDATED]);

  // NEW: single-select status filter ('all' or one of ACTIVE_STATUSES)
  const [statusFilter, setStatusFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const cylQ = query(
        collection(db, "returns_draft"),
        where("status", "in", ACTIVE_STATUSES),
        limit(FETCH_LIMIT_PER_COLLECTION)
      );
      const partsQ = query(
        collection(db, "returns_parts_draft"),
        where("status", "in", ACTIVE_STATUSES),
        limit(FETCH_LIMIT_PER_COLLECTION)
      );

      const [cylSnap, partsSnap] = await Promise.all([getDocs(cylQ), getDocs(partsQ)]);

      const cylRows = cylSnap.docs.map((d) => ({
        id: d.id,
        _col: "returns_draft",
        _kind: "cylinder",
        ...d.data(),
      }));

      const partRows = partsSnap.docs.map((d) => ({
        id: d.id,
        _col: "returns_parts_draft",
        _kind: "parts",
        ...d.data(),
      }));

      setAllRows([...cylRows, ...partRows]);
      setPageIndex(0);
    } catch (e) {
      console.error(e);
      setError("Failed to load drafts. You may need an index for (status, updatedAt).");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const counts = useMemo(() => {
    const byStatus = {};
    const byKind = { cylinder: 0, parts: 0 };
    for (const r of allRows) {
      byKind[r._kind] = (byKind[r._kind] || 0) + 1;
      const s = r.status || "new";
      byStatus[s] = (byStatus[s] || 0) + 1;
    }
    return { byStatus, byKind, total: allRows.length };
  }, [allRows]);

  // Map rows to unified view model
  const projectRow = (r) => {
    const f = r.form || {};
    const sMeta = statusOf(r.status);

    // parts fields
    const pCode = r.partCode || r.partTemplateCode || r.part?.partCode || "";
    const pTitle = r.partTitle || r.part?.title || "";

    const refText = f.returnsNoteNumber || r.returnsNoteNumber || "—";
    const sret = f.sretNumber || r.sretNumber || "—";
    const serialOrPart = r._kind === "parts" ? pCode || "—" : f.serialNumber || "—";
    const modelOrTitle = r._kind === "parts" ? pTitle || "—" : f.cylinderModel || "—";
    const reason =
      r._kind === "parts"
        ? f.faultFoundCode || f.reportedCode || "—"
        : r.reasonCode || f.reasonCode || "—";

    const createdAt = r.createdAt?.toDate?.() || null;
    const updatedAt = r.updatedAt?.toDate?.() || createdAt;

    return {
      raw: r,
      kindText: r._kind === "parts" ? "parts" : "cylinder",
      status: r.status || "new",
      statusMeta: sMeta,
      createdAt,
      updatedAt,
      refText,
      sret,
      serialOrPart,
      modelOrTitle,
      reason,
      progress: Math.round(r.progress || 0),
      rnNum: rnNumber(f.returnsNoteNumber || r.returnsNoteNumber || ""),
    };
  };

  // ----- RN exact numeric parsing (only numeric suffix) -----
  const rnNumQuery = useMemo(() => {
    const raw = rnQuery.trim();
    if (!raw) return null;
    const m = raw.match(/(\d+)$/); // accept "3" or "RN-000003" -> 3
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) ? n : null;
  }, [rnQuery]);

  // Filtering (status + exact RN numeric + optional general text)
  const filtered = useMemo(() => {
    const t = qtext.trim().toLowerCase();

    let base = allRows;

    // status filter
    if (statusFilter !== "all") {
      base = base.filter((r) => (r.status || "new") === statusFilter);
    }

    // RN exact numeric
    if (rnNumQuery !== null) {
      base = base.filter(
        (r) => rnNumber((r.form?.returnsNoteNumber) || r.returnsNoteNumber) === rnNumQuery
      );
      if (!t) return base;
    }

    if (!t) return base;

    return base.filter((r) => {
      const f = r.form || {};
      const note = f.returnsNoteNumber || r.returnsNoteNumber || "";
      const sret = (f.sretNumber || r.sretNumber || "").toLowerCase();
      const serial = (f.serialNumber || r.serialNumber || "").toLowerCase();

      if (r._kind === "parts") {
        const code  = (r.partCode || r.partTemplateCode || r.part?.partCode || "").toLowerCase();
        const title = (r.partTitle || r.part?.title || "").toLowerCase();
        const rep   = (f.reportedCode || "").toLowerCase();
        const found = (f.faultFoundCode || "").toLowerCase();

        return (
          code.includes(t) ||
          title.includes(t) ||
          rep.includes(t) ||
          found.includes(t) ||
          serial.includes(t) ||
          note.toLowerCase().includes(t) ||
          sret.includes(t) ||
          (r.id || "").toLowerCase().includes(t)
        );
      } else {
        const model  = (f.cylinderModel || "").toLowerCase();
        const reason = (f.reasonCode || r.reasonCode || "").toLowerCase();

        return (
          serial.includes(t) ||
          note.toLowerCase().includes(t) ||
          sret.includes(t) ||
          model.includes(t) ||
          reason.includes(t) ||
          (r.id || "").toLowerCase().includes(t)
        );
      }
    });
  }, [allRows, qtext, rnNumQuery, statusFilter]);

  const projected = useMemo(() => filtered.map(projectRow), [filtered]);

  // Sorting — simple dir-multiplied comparator + sane tiebreakers (dir-aware)
  const sorted = useMemo(() => {
    const dir = sortDir;

    const sortFn = (a, b) => {
      let res = 0;

      switch (sortBy) {
        case SORT_KEYS.TYPE:
          res = cmp(a.kindText, b.kindText);
          break;
        case SORT_KEYS.STATUS:
          res = cmp(statusRank(a.status), statusRank(b.status));
          break;
        case SORT_KEYS.CREATED:
          res = cmp(toMs(a.createdAt), toMs(b.createdAt));
          break;
        case SORT_KEYS.UPDATED:
          res = cmp(toMs(a.updatedAt), toMs(b.updatedAt));
          break;
        case SORT_KEYS.REF: {
          const an = a.rnNum, bn = b.rnNum;
          if (!Number.isNaN(an) && !Number.isNaN(bn)) {
            res = cmp(an, bn) || cmp(safeLower(a.refText), safeLower(b.refText));
          } else {
            res = cmp(safeLower(a.refText), safeLower(b.refText));
          }
          break;
        }
        case SORT_KEYS.SRET:
          res = cmp(safeLower(a.sret), safeLower(b.sret));
          break;
        case SORT_KEYS.SERIAL_OR_PART:
          res = cmp(safeLower(a.serialOrPart), safeLower(b.serialOrPart));
          break;
        case SORT_KEYS.MODEL_OR_TITLE:
          res = cmp(safeLower(a.modelOrTitle), safeLower(b.modelOrTitle));
          break;
        case SORT_KEYS.REASON:
          res = cmp(safeLower(a.reason), safeLower(b.reason));
          break;
        case SORT_KEYS.PROGRESS:
          res = cmp(Number(a.progress) || 0, Number(b.progress) || 0);
          break;
        default:
          res = 0;
      }

      if (res !== 0) return withDir(res, dir);

      // dir-aware tiebreakers so flips are visible
      const t1 = withDir(cmp(toMs(a.updatedAt), toMs(b.updatedAt)), dir);
      if (t1) return t1;
      const t2 = withDir(cmp(toMs(a.createdAt), toMs(b.createdAt)), dir);
      if (t2) return t2;
      return withDir(cmp(String(a.raw.id), String(b.raw.id)), dir);
    };

    return [...projected].sort(sortFn);
  }, [projected, sortBy, sortDir]);

  // paginate
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const clampedPageIndex = Math.min(pageIndex, pageCount - 1);
  const pageRows = sorted.slice(
    clampedPageIndex * PAGE_SIZE,
    clampedPageIndex * PAGE_SIZE + PAGE_SIZE
  );

  const open = (r) => {
    const route = r._kind === "parts" ? PART_ROUTE : CYL_ROUTE;
    navigate({ pathname: route, search: `?draft=${r.id}` });
  };

  const del = async (row) => {
    if (!window.confirm(`Delete draft ${row.id}?`)) return;
    await deleteDoc(doc(db, row._col, row.id));
    await refresh();
  };

  // First click = column default dir; second = flip; third = flip, etc.
  const clickSort = (key) => {
    setPageIndex(0);
    setSortBy((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prevKey; // same column -> flip
      }
      setSortDir(DEFAULT_DIR[key] || "asc"); // new column -> its default
      return key;
    });
  };

  const sortHeader = (label, key, extraClass = "") => (
    <th className={`px-3 py-2 ${extraClass}`}>
      <button
        type="button"
        onClick={() => clickSort(key)}
        className="inline-flex items-center gap-1 select-none hover:underline"
        title={`Sort by ${label}`}
      >
        <span>{label}</span>
        <span className="text-xs opacity-70">
          {sortBy === key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );

  // helper for clickable status chip
  const StatusChip = ({ s, count }) => {
    const m = statusOf(s);
    const active = statusFilter === s;
    return (
      <button
        type="button"
        onClick={() => {
          setStatusFilter((prev) => (prev === s ? "all" : s));
          setPageIndex(0);
        }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs transition
          ${m.badge} ${active ? "ring-2 ring-offset-1 ring-indigo-500" : "hover:bg-white"}`}
        title={`Show only ${m.label}`}
      >
        <span className={`inline-block w-2 h-2 rounded-full ${m.dot}`} />
        {m.label} ({count || 0})
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Sticky mobile-friendly search header */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-b">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <h1 className="text-2xl font-bold">Drafts</h1>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 md:ml-4">
            <input
              className="border rounded-md px-3 py-2 text-sm w-full"
              placeholder="Search (serial / SRET / model / reason / part / id)…"
              value={qtext}
              onChange={(e) => {
                setQtext(e.target.value);
                setPageIndex(0);
              }}
            />
            <input
              className="border rounded-md px-3 py-2 text-sm w-full"
              placeholder="Search RN (e.g. RN-000123 or 123) – exact"
              value={rnQuery}
              onChange={(e) => {
                setRnQuery(e.target.value);
                setPageIndex(0);
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(CYL_ROUTE)}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              title="New cylinder report"
            >
              + New Cylinder
            </button>
            <button
              onClick={() => navigate(PART_ROUTE)}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              title="New part report"
            >
              + New Part
            </button>
            <button
              onClick={refresh}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Legend row (clickable filters + counts) */}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600">
          {/* kinds */}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded border border-indigo-200 bg-indigo-100" />{" "}
            Cylinder ({counts.byKind.cylinder || 0})
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded border border-amber-200 bg-amber-100" />{" "}
            Part ({counts.byKind.parts || 0})
          </span>

          {/* "All" chip */}
          <button
            type="button"
            onClick={() => { setStatusFilter("all"); setPageIndex(0); }}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs transition
              ${statusFilter === "all" ? "ring-2 ring-offset-1 ring-indigo-500" : "hover:bg-gray-50"} bg-gray-100 text-gray-800 border-gray-200`}
            title="Show all statuses"
          >
            All ({counts.total})
          </button>

          {/* per-status chips */}
          <span className="inline-flex flex-wrap items-center gap-2">
            {ACTIVE_STATUSES.map((s) => (
              <StatusChip key={s} s={s} count={counts.byStatus[s]} />
            ))}
          </span>
        </div>
      </div>

      {error && <div className="mt-3 mb-3 text-red-600 text-sm">{error}</div>}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border rounded-lg overflow-hidden mt-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-2 py-2 w-3">{/* colour strip */}</th>
              {sortHeader("Type", SORT_KEYS.TYPE)}
              {sortHeader("Status", SORT_KEYS.STATUS)}
              {sortHeader("Created", SORT_KEYS.CREATED)}
              {sortHeader("Last Updated", SORT_KEYS.UPDATED)}
              {sortHeader("RN", SORT_KEYS.REF)}
              {sortHeader("SRET", SORT_KEYS.SRET)}
              {sortHeader("Serial / Part", SORT_KEYS.SERIAL_OR_PART)}
              {sortHeader("Model / Title", SORT_KEYS.MODEL_OR_TITLE)}
              {sortHeader("Reason", SORT_KEYS.REASON)}
              {sortHeader("Progress", SORT_KEYS.PROGRESS)}
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && allRows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={12}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && pageRows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={12}>
                  No drafts.
                </td>
              </tr>
            )}

            {pageRows.map(
              ({
                raw: r,
                statusMeta: sMeta,
                createdAt,
                updatedAt,
                refText,
                sret,
                serialOrPart,
                modelOrTitle,
                reason,
                progress,
              }) => {
                const tBadge = typeBadge(r._kind);
                return (
                  <tr key={`${r._col}:${r.id}`} className="border-t">
                    {/* coloured strip */}
                    <td className="px-2 py-2">
                      <span
                        className={`block w-1.5 h-6 rounded-full ${sMeta.dot}`}
                        title={sMeta.label}
                      />
                    </td>

                    {/* type */}
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${tBadge.cls}`}
                      >
                        {tBadge.text}
                      </span>
                    </td>

                    {/* status chip */}
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-2 px-2 py-0.5 rounded border ${sMeta.badge}`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full ${sMeta.dot}`} />
                        {sMeta.label}
                      </span>
                    </td>

                    <td className="px-3 py-2">{fmtDateTime(createdAt)}</td>
                    <td className="px-3 py-2">{fmtDateTime(updatedAt)}</td>

                    <td className="px-3 py-2 font-mono">{refText}</td>
                    <td className="px-3 py-2">{sret}</td>
                    <td className="px-3 py-2 font-mono">{serialOrPart}</td>
                    <td className="px-3 py-2">{modelOrTitle}</td>
                    <td className="px-3 py-2">{reason}</td>
                    <td className="px-3 py-2">{progress}%</td>

                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => open(r)}
                          className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                          title="Open draft"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => del(r)}
                          className="px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mt-4 grid grid-cols-1 gap-3">
        {loading && allRows.length === 0 && (
          <div className="text-sm text-gray-500">Loading…</div>
        )}
        {!loading && pageRows.length === 0 && (
          <div className="text-sm text-gray-500">No drafts.</div>
        )}

        {pageRows.map(
          ({
            raw: r,
            statusMeta: sMeta,
            createdAt,
            updatedAt,
            refText,
            sret,
            serialOrPart,
            modelOrTitle,
            reason,
            progress,
          }) => {
            const tBadge = typeBadge(r._kind);
            return (
              <div
                key={`${r._col}:${r.id}`}
                className="border rounded-lg p-3 bg-white shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-5 rounded-full ${sMeta.dot}`} />
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium border ${tBadge.cls}`}
                    >
                      {tBadge.text}
                    </span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${sMeta.badge}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${sMeta.dot}`} />
                    {sMeta.label}
                  </span>
                </div>

                <div className="mt-2">
                  <div className="text-sm">
                    <span className="font-medium">RN:</span>{" "}
                    <span className="font-mono">{refText}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">SRET:</span> {sret}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Serial/Part:</span>{" "}
                    <span className="font-mono">{serialOrPart}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Model/Title:</span> {modelOrTitle}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Reason:</span> {reason}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Progress:</span> {progress}%
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Created:</span> {fmtDateTime(createdAt)}
                  </div>
                  <div>
                    <span className="font-medium">Updated:</span> {fmtDateTime(updatedAt)}
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => open(r)}
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                    title="Open draft"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => del(r)}
                    className="px-3 py-2 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          }
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center gap-2 justify-center md:justify-start">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || clampedPageIndex === 0}
          onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">
          Page {clampedPageIndex + 1} / {pageCount}
        </span>
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || clampedPageIndex >= pageCount - 1}
          onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}
